// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import { Permission } from '@/authorization/types';
import { PostAddRounded } from '@/constants/hardfin-standard-icons';
import { ProjectCost } from '@/project/api/types';
import {
  ProjectCostTableHoursRow,
  ProjectCostTableRow,
} from '@/project/components/project-cost-table-row';
import { SparseProject } from '@/sparse/api/types';
import {
  Box,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      sx={{ width: '100%', height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            width: '100%',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

interface Props {
  /**
   * The project to which the costs belong.
   */
  project: SparseProject;

  /**
   * A collection of costs to display in the table.
   */
  costs: ProjectCost[];

  selectedTab: number;
}

interface SubTableProps {
  /**
   * The project to which the costs belong.
   */
  project: SparseProject;

  /**
   * A collection of costs to display in the table.
   */
  costs: ProjectCost[];

  /**
   * Whether the user can view time rates
   */
  canViewRates: boolean;
}

function AllProjectCosts(props: SubTableProps) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="10%">Date</TableCell>
            <TableCell width="60%">Description</TableCell>
            <TableCell width="10%" align={'right'}>
              Amount
            </TableCell>
            <TableCell width="10%" align={'center'}>
              Info
            </TableCell>
            <TableCell align="center" width="10%">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.costs.map((cost) => (
            <ProjectCostTableRow
              key={cost.key}
              cost={cost}
              project={props.project}
              canViewRates={props.canViewRates}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function NonTimeProjectCosts(props: SubTableProps) {
  if (props.costs.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center',
          width: '100%',
          minHeight: '200px',
        }}
      >
        <PostAddRounded
          sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No other costs yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: '400px', marginBottom: 3 }}
        >
          Track and manage additional costs associated with this project
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="10%">Date</TableCell>
            <TableCell width="60%">Description</TableCell>
            <TableCell width="10%" align={'right'}>
              Amount
            </TableCell>
            <TableCell width="10%" align={'center'}>
              Info
            </TableCell>
            <TableCell align="center" width="10%">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.costs.map((cost) => (
            <ProjectCostTableRow
              key={cost.key}
              cost={cost}
              project={props.project}
              canViewRates={props.canViewRates}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TimeProjectCosts(props: SubTableProps) {
  if (props.costs.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center',
          width: '100%',
          minHeight: '200px',
        }}
      >
        <PostAddRounded
          sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No time costs yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: '400px', marginBottom: 3 }}
        >
          Time costs help you track information about cost incurred on this
          project through time spent
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={props.canViewRates ? '15%' : '20%'}>
              Employee
            </TableCell>
            <TableCell width={props.canViewRates ? '10%' : '15%'}>
              Date
            </TableCell>
            <TableCell width={props.canViewRates ? '30%' : '40%'}>
              Description
            </TableCell>
            <TableCell width={props.canViewRates ? '10%' : '15%'}>
              Duration
            </TableCell>
            {props.canViewRates && (
              <TableCell width="10%" align={'right'}>
                Rate
              </TableCell>
            )}
            {props.canViewRates && (
              <TableCell width="10%" align={'right'}>
                Total cost
              </TableCell>
            )}
            <TableCell width="5%" align={'center'}>
              Info
            </TableCell>
            <TableCell align="center" width="10%">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.costs.map((cost) => (
            <ProjectCostTableHoursRow
              key={cost.key}
              cost={cost}
              project={props.project}
              canViewRates={props.canViewRates}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * A table that displays a collection of project costs with options to edit
 * or delete each cost.
 */
export function ProjectCostTable(props: Props) {
  const { hasPermission } = usePermissionChecker();

  // Check if user has permission to view time rates
  const canViewRates =
    hasPermission('time.rate', 'read') === Permission.PermissionExplicitTrue ||
    hasPermission('time.rate', 'read') === Permission.PermissionImplicitTrue;

  if (props.costs.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center',
          minHeight: '200px',
        }}
      >
        <PostAddRounded
          sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No costs yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: '400px', marginBottom: 3 }}
        >
          Track and manage costs associated with this project
        </Typography>
      </Card>
    );
  }

  return (
    <>
      <TabPanel index={0} value={props.selectedTab}>
        <AllProjectCosts
          costs={props.costs}
          project={props.project}
          canViewRates={canViewRates}
        />
      </TabPanel>
      <TabPanel index={1} value={props.selectedTab}>
        <TimeProjectCosts
          costs={props.costs.filter((cost) => cost.type === 'TIME')}
          project={props.project}
          canViewRates={canViewRates}
        />
      </TabPanel>
      <TabPanel index={2} value={props.selectedTab}>
        <NonTimeProjectCosts
          costs={props.costs.filter((cost) => cost.type !== 'TIME')}
          project={props.project}
          canViewRates={canViewRates}
        />
      </TabPanel>
    </>
  );
}
